import classnames from 'classnames'
import { Status } from 'embed/components/ApplicationField/Status'
import { Field, FieldData, FieldDataValue, TextFieldOptions } from 'embed/constants'
import { css } from 'emotion'
import * as React from 'react'
import { useTheme } from 'embed/hooks'
import { FloatingLabel } from '../ApplicationField/FloatingLabel'

const ClearIcon = require('assets/components/minus-in-filled-circle.svg')

const ADD_TRIGGER = ','
const INITIAL_MARGIN = 85

const styles = require('./styles.scss')
const applicationFieldStyles = require('../ApplicationField/styles.scss')

export interface FreeFormMultipleProps {
  onChange: (value: FieldDataValue) => void
  options: TextFieldOptions
  value?: string[]
  name: string
  field: Field
  fieldData?: FieldData
  useFloatingLabel?: boolean
}

type Props = FreeFormMultipleProps

const isInputValid = (value) => value !== undefined && value !== null && value.length > 0

const isValueRequired = (value): boolean => {
  if (Array.isArray(value) && value.length > 0) {
    return false
  }
  return !isInputValid(value)
}
const getShouldShowValidation = (isDirty: boolean, fieldData?: FieldData, value?: string[]): boolean => {
  if (fieldData?.shouldShowValidationStatus) {
    return !fieldData.isValid
  }
  if (Array.isArray(value)) {
    return value.length > 0
  }
  return isDirty
}

export const FreeformMultiple: React.FC<Props> = ({ field, fieldData, name, onChange, useFloatingLabel, value }) => {
  const [addButtonDisabled, setAddButtonDisabled] = React.useState(true)
  const [inputValue, setInputValue] = React.useState<string | undefined>('')
  const [isDirty, setIsDirty] = React.useState(false)
  const [isRequired, setIsRequired] = React.useState(isValueRequired(value))

  const handleAdd = React.useCallback(
    (e) => {
      e.preventDefault()
      if (inputValue) {
        const addedValue = inputValue
        const newSet = new Set(Array.isArray(value) ? [...value, addedValue] : [addedValue])
        const newValue = Array.from(newSet)
        setInputValue('')
        setAddButtonDisabled(true)
        setIsRequired(isValueRequired(newValue))
        onChange(newValue)
      } else {
        setIsDirty(true)
      }
    },
    [inputValue, onChange, value],
  )

  const handleRemove = React.useCallback(
    (val) => {
      if (Array.isArray(value)) {
        const newSet = new Set(value)
        newSet.delete(val)
        const newValue = Array.from(newSet)
        setInputValue('')
        setAddButtonDisabled(true)
        setIsRequired(isValueRequired(newValue))
        onChange(newValue)
      }
    },
    [onChange, value],
  )

  const onInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const newValue = event ? event.currentTarget.value : undefined
    const valueLength = newValue?.length
    if (newValue !== undefined && valueLength !== undefined && newValue[valueLength - 1] === ADD_TRIGGER) {
      handleAdd(event)
    } else {
      setInputValue(newValue)
      setAddButtonDisabled(!isInputValid(newValue))
      setIsRequired(isValueRequired(newValue))
    }
  }

  const {
    borderRadiusSmall,
    backdropStyles,
    colorPrimary,
    colorTextPrimary,
    colorTextSecondary,
    errorStyles,
    fontFamilyPrimary,
    fontSizeBase,
  } = useTheme()
  return (
    <div
      className={classnames(
        css`
          background-color: ${backdropStyles.backgroundColor};
          font-color: ${colorTextPrimary};
          font-family: ${fontFamilyPrimary};
          font-size: ${fontSizeBase};
        `,
      )}
    >
      {!useFloatingLabel && (
        <div className={styles.freeformLabelContainer}>
          <div className={styles.freeformLabel}>Medication Name</div>
          <Status
            colorError={errorStyles?.color}
            colorTextSecondary={colorTextSecondary}
            field={field}
            fontFamilyPrimary={fontFamilyPrimary}
            isValid={!isRequired}
            shouldShowIsValid={false}
            shouldShowIsInvalid={getShouldShowValidation(isDirty, fieldData, value)}
            marginRight={INITIAL_MARGIN}
          />
        </div>
      )}
      <form onSubmit={handleAdd}>
        <div
          className={classnames(styles.freeformField, {
            [applicationFieldStyles.floatingLabelContainer]: useFloatingLabel,
          })}
        >
          <input
            type="text"
            name={name}
            className={classnames(
              styles.freeformInput,
              css`
                border-radius: ${borderRadiusSmall};
                font-size: ${fontSizeBase};
              `,
              getShouldShowValidation(isDirty, fieldData, value)
                ? css`
                    outline-color: ${errorStyles?.color};
                    border-color: ${errorStyles?.color};
                  `
                : '',
              { [applicationFieldStyles.floatingLabelInput]: useFloatingLabel },
            )}
            required={useFloatingLabel}
            value={inputValue}
            onChange={onInputChange}
          />
          <FloatingLabel
            useFloatingLabelInputs={useFloatingLabel}
            fieldOptions={{ ...field.options, label: 'Medication Name' }}
          />
          <button
            type="submit"
            className={classnames(
              styles.addButton,
              css`
                background-color: ${colorPrimary};
                border-radius: ${borderRadiusSmall};
                font-family: ${fontFamilyPrimary};
              `,
              addButtonDisabled ? styles.disabled : '',
            )}
          >
            Add
          </button>
        </div>
      </form>
      {getShouldShowValidation(isDirty, fieldData, value) && (
        <div
          className={classnames(
            styles.validation,
            css`
              color: ${errorStyles?.color};
            `,
          )}
        >
          Please add at least one item
        </div>
      )}
      <div className={styles.freeformItemContainer}>
        {Array.isArray(value) &&
          value.map((val, index) => (
            <div key={`div_${val}_${index}`} className={classnames(styles.freeformItem)}>
              <label key={`label_${val}_${index}`} className={classnames(styles.freeformItemLabel)}>
                {val}
              </label>
              <button
                className={classnames(
                  styles.removeButton,
                  css`
                    background-color: ${backdropStyles.backgroundColor};
                    border-radius: ${borderRadiusSmall};
                  `,
                )}
                onClick={() => handleRemove(val)}
              >
                <ClearIcon />
              </button>
            </div>
          ))}
      </div>
    </div>
  )
}
